/** Wire protocol between the floor page and the OfficeFloor Durable Object. */

export const OFFICE_TICK_MS = 100;
/** Clients send at most this often while moving. */
export const OFFICE_SEND_INTERVAL_MS = 100;

export const OFFICE_STATUSES = ['available', 'away', 'focusing'] as const;
export type OfficeStatus = (typeof OFFICE_STATUSES)[number];

export const OFFICE_STATUS_INFO: Record<OfficeStatus, { label: string; description: string; color: string }> = {
  available: { label: 'Available', description: 'People nearby can talk to you.', color: '#1f9d55' },
  away: { label: 'Away', description: 'Camera and microphone off; you still hear people.', color: '#d9820b' },
  focusing: { label: 'Focusing', description: 'Camera, microphone and sound off. Knock to interrupt.', color: '#7c63f0' },
};

export interface OfficePerson {
  userId: string;
  name: string;
  image: string | null;
  x: number;
  y: number;
  /** Facing angle in degrees, 0 = right, 90 = down. */
  h: number;
  status: OfficeStatus;
  statusText: string;
  roomId: string | null;
  mic: boolean;
  camera: boolean;
  /** Sharing their screen. */
  screen: boolean;
  speaking: boolean;
  hand: boolean;
  /** When the hand went up, so a queue can be ordered. */
  handAt: number | null;
  /** Heard by everyone on the floor, wherever they stand. */
  broadcasting: boolean;
  /** Recording the conversation around them. */
  recording: boolean;
  joinedAt: number;
}

/** Live state of a walled room: whether its door is closed and how many people are inside. */
export interface RoomState {
  roomId: string;
  closed: boolean;
  closedBy: string | null;
  count: number;
}

export type RoomBlockReason = 'closed' | 'full' | 'private';

export const MAX_CHAT_TEXT = 500;
/** How long an admission through a closed door stays valid. */
export const ADMIT_TTL_MS = 60_000;

/**
 * How live media travels. `sfu` is the Cloudflare Realtime SFU (needs the secrets in BLOCKERS.md 7);
 * `mesh` is direct browser-to-browser WebRTC with the floor relaying the handshake, which works
 * without any setup and is fine for a handful of people in earshot.
 */
export type RtcMode = 'sfu' | 'mesh';

/** The kinds of track a person can publish. */
export const MEDIA_KINDS = ['mic', 'camera', 'screen', 'screenAudio'] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];

/** An ICE candidate as the browser serialises it (shared code has no DOM types). */
export interface IceCandidate {
  candidate: string;
  sdpMid?: string | null;
  sdpMLineIndex?: number | null;
  usernameFragment?: string | null;
}

export interface IceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

/** Mesh signalling payloads relayed between two people in earshot. */
export type RtcSignal =
  | { type: 'offer'; sdp: string }
  | { type: 'answer'; sdp: string }
  | { type: 'ice'; candidate: IceCandidate | null }
  /** Tells the peer which of my transceivers carries what, by mid. */
  | { type: 'tracks'; mids: Record<string, MediaKind> };

export type OfficeClientMessage =
  | { t: 'move'; x: number; y: number; h: number }
  | { t: 'status'; status: OfficeStatus; text?: string }
  | { t: 'media'; mic: boolean; camera: boolean; screen?: boolean }
  | { t: 'speaking'; on: boolean }
  | { t: 'hand'; raised: boolean }
  | { t: 'react'; emoji: string }
  | { t: 'nudge'; userId: string }
  /** Mesh handshake to one person. Only relayed while the two are in earshot. */
  | { t: 'rtc'; to: string; signal: RtcSignal }
  /** Close or open the door of the room I am in. */
  | { t: 'door'; roomId: string; closed: boolean }
  | { t: 'knock'; roomId: string }
  /** Someone inside answers a knock. */
  | { t: 'admit'; roomId: string; userId: string; ok: boolean }
  /** Speak to the whole floor. */
  | { t: 'broadcast'; on: boolean }
  /** Recording the people in earshot (they are told). */
  | { t: 'recording'; on: boolean }
  /** Send everyone in my room into other rooms, or bring them back. */
  | { t: 'breakout'; from: string; roomIds: string[] }
  | { t: 'regroup'; to: string }
  /** Mute one person, or everyone in my room when null. */
  | { t: 'mute'; userId: string | null }
  | { t: 'follow'; userId: string }
  | { t: 'followReply'; userId: string; ok: boolean }
  /** A line of chat to the people in earshot (or my room). Not stored. */
  | { t: 'chat'; text: string }
  | { t: 'ping' };

/** A moved person in a frame: user id, x, y, heading. */
export type OfficeMove = [userId: string, x: number, y: number, h: number];

export type OfficeServerMessage =
  | { t: 'hello'; you: string; people: OfficePerson[]; tickMs: number; radius: number; rtc: RtcMode; rooms: RoomState[] }
  | { t: 'joined'; person: OfficePerson }
  | { t: 'left'; userId: string }
  | { t: 'frame'; m: OfficeMove[] }
  | { t: 'person'; userId: string; patch: Partial<Pick<OfficePerson, 'status' | 'statusText' | 'roomId' | 'mic' | 'camera' | 'screen' | 'speaking' | 'hand' | 'handAt' | 'broadcasting' | 'recording'>> }
  /** I was moved by a breakout or a regroup; the client snaps to the spot. */
  | { t: 'moved'; x: number; y: number; roomId: string | null; reason: 'breakout' | 'regroup' }
  /** Who the receiving person can hear right now. Sent only when the set changes. */
  | { t: 'near'; userIds: string[] }
  /** The server refused a move; the client snaps back. */
  | { t: 'correct'; x: number; y: number; reason?: 'speed' | 'wall' | 'layout' }
  /** A move into a room was refused for a reason the person should see. */
  | { t: 'blocked'; roomId: string; reason: RoomBlockReason }
  | { t: 'rooms'; rooms: RoomState[] }
  /** Someone knocked on my room's door. */
  | { t: 'knock'; roomId: string; from: string }
  | { t: 'admitted'; roomId: string }
  | { t: 'denied'; roomId: string }
  | { t: 'react'; userId: string; emoji: string }
  | { t: 'nudge'; from: string }
  | { t: 'muted'; by: string }
  | { t: 'follow'; from: string }
  | { t: 'followReply'; userId: string; ok: boolean }
  | { t: 'chat'; from: string; text: string; at: number }
  | { t: 'rtc'; from: string; signal: RtcSignal }
  | { t: 'pong' };

/** Realtime SFU session bookkeeping: the Worker proxies these for the browser (secret stays server side). */
export interface RtcConfigDTO {
  mode: RtcMode;
  iceServers: IceServer[];
}

export interface RtcTrackRef {
  userId: string;
  kind: MediaKind;
}

/** A track as the SFU names it: `<userId>/<kind>`. */
export function sfuTrackName(userId: string, kind: MediaKind): string {
  return `${userId}/${kind}`;
}

/** Close codes the floor uses. */
export const OFFICE_CLOSE = {
  /** The same person joined from another tab, which took over. */
  replaced: 4010,
  /** The floor changed (layout, deletion, membership): refetch and connect again. */
  reload: 4011,
  /** Nowhere free to arrive (the start point sits inside a room this person may not enter). Do not retry. */
  noEntry: 4012,
} as const;

export const MAX_STATUS_TEXT = 60;
export const OFFICE_REACTIONS = ['👍', '👏', '😂', '❤️', '🎉', '👋'] as const;
